import { useCallback, useState } from 'react';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Tab,
  Tabs,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react/offline';
import RefreshIcon from '@iconify-icons/tabler/refresh';
import ChangeIcon from '@iconify-icons/tabler/arrows-exchange';
import CompleteIcon from '@iconify-icons/tabler/circle-check';
import CloseIcon from '@iconify-icons/tabler/x';
import {
  getShipmentCaseDetail,
  getShipmentCaseOptions,
  type CaseAction,
  type CaseCommandResult,
  type ShipmentCase,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, LoadingState, StatusChip } from '../../components/ui';
import { useScopedRequest } from '../shipments/arrival-manager/use-scoped-request';
import {
  caseCargoSummary,
  caseKinds,
  caseStatuses,
  confidenceLabels,
  outcomes,
  caseLabel,
} from './model';
import CaseForm from './CaseForm';
import CaseHistory from './CaseHistory';
import CaseNoteComposer from './CaseNoteComposer';
import CaseRequests from './CaseRequests';
import CaseCargo from './CaseCargo';

type Editor = { action: CaseAction['action']; inputId?: number };
const editorTitles: Partial<Record<CaseAction['action'], string>> = {
  request_input: 'Solicitar información',
  receive_input: 'Registrar respuesta',
  responsible: 'Editar responsable de la pérdida',
  escalate: 'Cambiar tipo',
  close: 'Dar por cerrado el caso',
  assign_input: 'Editar encargado de seguimiento',
  reopen: 'Reabrir caso',
};
export default function CaseDetailModal({
  record,
  identity,
  onClose,
  onSaved,
  onReload,
}: {
  record: ShipmentCase;
  identity: string;
  onClose: () => void;
  onSaved: (r: CaseCommandResult) => void;
  onReload: () => void;
}) {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [tab, setTab] = useState('inputs');
  const [locked, setLocked] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [savedVersion, setSavedVersion] = useState(0);
  const load = useCallback(
    async (signal: AbortSignal) => {
      const [detail, options] = await Promise.all([
        getShipmentCaseDetail(record, record.id, signal),
        getShipmentCaseOptions(record, '', record.shipment_id, signal),
      ]);
      return { detail, options };
    },
    [record]
  );
  const data = useScopedRequest(
    `${identity}:${record.id}:${record.version}`,
    load
  );
  const refreshing = savedVersion > record.version;
  const pending =
    data.data?.detail.inputs.filter((input) => !input.received_at) || [];
  const disabled =
    locked || refreshing || data.loading || Boolean(data.error) || !data.data;
  const closed = record.status === 'closed';
  const locale = currentLanguage === 'en' ? 'en-US' : 'es-CL';
  const number = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
  const summary = caseCargoSummary(
    record.items,
    data.data?.options.cargo || []
  );
  const created = new Date(record.created_at);
  const createdLabel = Number.isFinite(created.getTime())
    ? `${new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(created)} · ${new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(created)}`
    : '—';
  const ageSeconds = Math.max(
    0,
    Math.floor((Date.now() - created.getTime()) / 1000)
  );
  const relativeCreated = Number.isFinite(created.getTime())
    ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
        -Math.floor(
          ageSeconds /
            (ageSeconds >= 86400
              ? 86400
              : ageSeconds >= 3600
                ? 3600
                : ageSeconds >= 60
                  ? 60
                  : 1)
        ),
        ageSeconds >= 86400
          ? 'day'
          : ageSeconds >= 3600
            ? 'hour'
            : ageSeconds >= 60
              ? 'minute'
              : 'second'
      )
    : '';
  const cargoUnits = summary.pallets
    ? `${summary.pallets} ${t(summary.pallets === 1 ? 'pallet' : 'pallets')}${summary.withoutPallet ? ` · ${summary.withoutPallet} ${t('líneas sin pallet')}` : ''}`
    : `${record.items.length} ${t(record.items.length === 1 ? 'línea' : 'líneas')}`;
  const infoId = `case-info-${record.id}`;
  function edit(action: CaseAction['action'], inputId?: number) {
    if (!disabled) setEditor({ action, inputId });
  }
  function closeModal() {
    if (!locked && !editor) onClose();
  }
  function saved(result: CaseCommandResult) {
    setEditor(null);
    setLocked(false);
    setSavedVersion(result.version);
    onSaved(result);
  }
  const waiting = data.loading || refreshing;
  const readState = waiting ? (
    <div className='flex flex-col items-center gap-3'>
      <LoadingState label={t('Cargando caso…')} />
      {refreshing && (
        <Button size='sm' variant='light' onPress={onReload}>
          {t('Reintentar')}
        </Button>
      )}
    </div>
  ) : data.error ? (
    <Alert variant='danger' showIcon>
      {data.error}
      <Button variant='flat' onPress={() => void data.reload()}>
        {t('Reintentar')}
      </Button>
    </Alert>
  ) : null;
  return (
    <>
      <Modal
        isOpen
        size='5xl'
        placement='center'
        scrollBehavior='inside'
        isDismissable={!locked && !editor}
        isKeyboardDismissDisabled={locked || Boolean(editor)}
        hideCloseButton
        onClose={closeModal}
        classNames={{
          base: 'm-4 max-w-[980px] h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] overflow-hidden md:h-[640px] md:max-h-[calc(100dvh-4rem)]',
        }}
      >
        <ModalContent>
          <ModalHeader className='sr-only'>
            {caseLabel(record)} · {record.current_reference}
          </ModalHeader>
          <div className='grid min-h-0 min-w-0 flex-1 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] md:grid-cols-[300px_minmax(0,1fr)] md:grid-rows-1'>
            <aside
              className='flex max-h-[52dvh] min-h-0 min-w-0 flex-col border-b border-default-200 bg-background/70 md:max-h-none md:border-b-0 md:border-r'
              aria-label={t('Datos del caso')}
            >
              <div className='shrink-0 px-5 pt-5'>
                <div className='flex flex-wrap items-center gap-2'>
                  <StatusChip
                    tone={
                      record.kind === 'observation'
                        ? 'warning'
                        : record.kind === 'incident'
                          ? 'danger'
                          : 'info'
                    }
                  >
                    {t(caseKinds[record.kind])}
                  </StatusChip>
                  <StatusChip
                    tone={
                      closed
                        ? 'default'
                        : record.status === 'waiting'
                          ? 'info'
                          : 'success'
                    }
                  >
                    <span className='mr-1.5 inline-block size-1.5 rounded-full bg-current' />
                    {t(caseStatuses[record.status])}
                  </StatusChip>
                </div>
                <p className='mt-3 font-mono text-xs text-default-400'>
                  {caseLabel(record)}
                </p>
                <p className='mt-0.5 break-words font-mono text-lg font-bold leading-tight'>
                  {record.current_reference}
                </p>
                <p className='mt-2 break-words text-[13px] font-semibold'>
                  {record.shipment_snapshot.receiver || '—'}{' '}
                  <span className='font-normal text-default-400'>
                    · {record.shipment_snapshot.container || '—'}
                  </span>
                </p>
                <Button
                  size='sm'
                  variant='light'
                  className='my-2 h-7 px-0 md:hidden'
                  aria-expanded={showInfo}
                  aria-controls={infoId}
                  onPress={() => setShowInfo(!showInfo)}
                >
                  {t(
                    showInfo
                      ? 'Ocultar datos y acciones'
                      : 'Ver datos y acciones'
                  )}
                </Button>
              </div>
              <div
                id={infoId}
                className={`min-h-0 flex-1 overflow-y-auto px-5 pb-2 ${showInfo ? 'block' : 'hidden'} md:block`}
              >
                <div className='mt-4 flex flex-col gap-2.5 border-t border-default-200 pt-4'>
                  <div>
                    <div className='flex items-baseline justify-between gap-2'>
                      <p className='text-[11px] text-default-400'>
                        {t('Responsable de la pérdida')}
                      </p>
                      {!closed && (
                        <Button
                          size='sm'
                          color='primary'
                          variant='light'
                          className='h-5 min-w-0 px-0 text-[11px]'
                          aria-label={t('Editar responsable de la pérdida')}
                          isDisabled={disabled}
                          onPress={() => edit('responsible')}
                        >
                          {t('Editar')}
                        </Button>
                      )}
                    </div>
                    <p className='mt-0.5 text-[13px] font-semibold'>
                      {record.responsible
                        ? t(record.responsible)
                        : t('Sin asignar')}
                      {record.responsible_name
                        ? ` · ${record.responsible_name}`
                        : ''}
                    </p>
                  </div>
                  <div>
                    <p className='text-[11px] text-default-400'>
                      {t('Motivos')}
                    </p>
                    <ul className='mt-1 text-[13px] leading-snug'>
                      {record.reasons.map((reason) => (
                        <li key={reason}>{t(reason)}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className='text-[11px] text-default-400'>
                      {t('Carga afectada')}
                    </p>
                    <p className='mt-1 font-mono text-[13px] font-semibold'>
                      {number(summary.kilos)} kg{' '}
                      <span className='font-sans text-xs font-normal text-default-400'>
                        · {cargoUnits}
                      </span>
                    </p>
                  </div>
                  {record.description && (
                    <div>
                      <p className='text-[11px] text-default-400'>
                        {t('Descripción')}
                      </p>
                      <p className='mt-1 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-default-500'>
                        {record.description}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className='text-[11px] text-default-400'>
                      {t('Ingresado')}
                    </p>
                    <p className='mt-1 text-[11px]'>
                      {createdLabel}{' '}
                      <span className='text-default-400'>
                        · {relativeCreated}
                      </span>
                    </p>
                  </div>
                  {record.closed_at && (
                    <div>
                      <p className='text-[11px] text-default-400'>
                        {t('Resultado')}
                      </p>
                      <p className='mt-1 text-[13px]'>
                        {record.outcome && t(outcomes[record.outcome])}
                      </p>
                      <p className='mt-1 whitespace-pre-wrap break-words text-xs text-default-400'>
                        {record.closure_note}
                      </p>
                    </div>
                  )}
                  {record.valuation && (
                    <div>
                      <p className='text-[11px] text-default-400'>
                        {t('Valorización vigente')}
                      </p>
                      <p className='mt-1 text-[13px]'>
                        {new Intl.NumberFormat(locale, {
                          style: 'currency',
                          currency: 'USD',
                        }).format(record.valuation.amount_usd)}{' '}
                        · {t(confidenceLabels[record.valuation.confidence])}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div
                className={`shrink-0 px-5 pb-4 pt-4 ${showInfo ? 'block' : 'hidden'} md:block`}
              >
                <p className='mb-2 text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                  {t('Acciones del caso')}
                </p>
                <div className='flex flex-col gap-2'>
                  {closed ? (
                    <Button
                      size='sm'
                      color='primary'
                      isDisabled={disabled}
                      onPress={() => edit('reopen')}
                    >
                      {t('Reabrir caso')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        size='sm'
                        color='primary'
                        className='h-9 text-[13px]'
                        startContent={<Icon icon={CompleteIcon} />}
                        isDisabled={disabled}
                        onPress={() => edit('close')}
                      >
                        {t('Dar por cerrado el caso')}
                      </Button>
                      <Button
                        size='sm'
                        variant='light'
                        className='h-9 border border-default-200 text-[13px]'
                        startContent={<Icon icon={ChangeIcon} />}
                        isDisabled={disabled}
                        onPress={() => edit('escalate')}
                      >
                        {t('Cambiar tipo')}
                      </Button>
                    </>
                  )}
                  <Button
                    size='sm'
                    variant='light'
                    startContent={<Icon icon={RefreshIcon} />}
                    isDisabled={locked}
                    onPress={onReload}
                  >
                    {t('Actualizar caso')}
                  </Button>
                </div>
              </div>
            </aside>
            <div className='relative flex min-h-0 min-w-0 flex-col'>
              <Tooltip content={t('Cerrar')}>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  className='absolute right-2 top-2 z-10 size-8 min-w-8'
                  aria-label={t('Cerrar ficha')}
                  isDisabled={locked || Boolean(editor)}
                  onPress={closeModal}
                >
                  <Icon icon={CloseIcon} width={16} />
                </Button>
              </Tooltip>
              <Tabs
                aria-label={t('Información del caso')}
                variant='underlined'
                destroyInactiveTabPanel={false}
                selectedKey={tab}
                onSelectionChange={(key) => setTab(String(key))}
                isDisabled={locked}
                classNames={{
                  base: 'w-full shrink-0 border-b border-default-200 pl-4 pr-12',
                  tabList: 'max-w-full gap-0 overflow-x-auto rounded-none p-0',
                  tab: 'h-[52px] px-2.5 text-[13px]',
                  cursor: 'bg-primary',
                  panel: 'min-h-0 flex-1 overflow-y-auto p-5 md:px-6',
                }}
              >
                <Tab
                  key='inputs'
                  aria-label={`${t('Solicitudes')} (${pending.length})`}
                  title={
                    <span className='flex items-center gap-1.5'>
                      {t('Solicitudes')}
                      <span className='font-mono text-[11px] text-default-400'>
                        {data.data ? pending.length : record.pending_inputs}
                      </span>
                    </span>
                  }
                >
                  {readState ||
                    (data.data && (
                      <CaseRequests
                        detail={data.data.detail}
                        identity={`${identity}:${record.id}`}
                        closed={closed}
                        disabled={disabled}
                        onRequest={() => edit('request_input')}
                        onReply={(id) => edit('receive_input', id)}
                        onAssign={(id) => edit('assign_input', id)}
                        onHistory={() => setTab('history')}
                      />
                    ))}
                </Tab>
                <Tab key='history' title={t('Historial')}>
                  {readState ||
                    (data.data && (
                      <div className='flex flex-col gap-5'>
                        {!closed && (
                          <CaseNoteComposer
                            key={record.version}
                            record={record}
                            identity={`${identity}:${record.id}:${record.version}:note`}
                            onSaved={saved}
                            onLockedChange={setLocked}
                          />
                        )}
                        <CaseHistory
                          detail={data.data.detail}
                          identity={`${identity}:${record.id}`}
                        />
                      </div>
                    ))}
                </Tab>
                <Tab
                  key='cargo'
                  aria-label={t('Carga afectada')}
                  title={
                    <span className='flex items-center gap-1.5'>
                      {t('Carga afectada')}
                      <span className='font-mono text-[11px] text-default-400'>
                        {record.items.length}
                      </span>
                    </span>
                  }
                >
                  {readState ||
                    (data.data && (
                      <CaseCargo
                        record={record}
                        currentCargo={data.data.options.cargo}
                      />
                    ))}
                </Tab>
                <Tab
                  key='valuation'
                  aria-label={t('Valorizar · Pronto')}
                  isDisabled
                  title={
                    <span className='flex items-center gap-1.5'>
                      {t('Valorizar')}
                      <Chip
                        size='sm'
                        variant='flat'
                        classNames={{ base: 'h-4', content: 'text-[9px] px-0' }}
                      >
                        {t('Pronto')}
                      </Chip>
                    </span>
                  }
                />
              </Tabs>
              <div className='flex shrink-0 justify-end border-t border-default-200 px-5 py-2'>
                <Button
                  size='sm'
                  variant='light'
                  isDisabled={locked || Boolean(editor)}
                  onPress={closeModal}
                >
                  {t('Cerrar')}
                </Button>
              </div>
            </div>
          </div>
        </ModalContent>
      </Modal>
      {editor && data.data && (
        <Modal
          isOpen
          size='2xl'
          scrollBehavior='inside'
          isDismissable={!locked}
          isKeyboardDismissDisabled={locked}
          hideCloseButton={locked}
          onClose={() => {
            if (!locked) setEditor(null);
          }}
        >
          <ModalContent>
            <ModalHeader>
              {t(editorTitles[editor.action] || editor.action)}
            </ModalHeader>
            <ModalBody className='pb-6'>
              <CaseForm
                key={`${editor.action}:${editor.inputId || ''}:${record.version}`}
                scope={record}
                identity={`${identity}:${record.id}:${record.version}:${editor.action}:${editor.inputId || ''}`}
                action={editor.action}
                record={record}
                options={data.data.options}
                pendingInputs={pending}
                selectedInputId={editor.inputId}
                onLockedChange={setLocked}
                onSaved={saved}
                onCancel={() => setEditor(null)}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
